import React from 'react';

const money = (value) => `Rs. ${parseFloat(value || 0).toFixed(2)}`;
const points = (value) => `${parseFloat(value || 0).toFixed(2)} pts`;

const dateParts = (value) => {
  if (!value) return { date: '', time: '' };
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return { date: '', time: '' };
  return {
    date: date.toLocaleDateString('en-GB'),
    time: date.toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    }),
  };
};

const rowStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  gap: '8px',
  fontSize: '11px',
};

const dividerStyle = {
  borderTop: '1px dashed #111',
  margin: '8px 0',
};

const tableHeaderCell = {
  fontSize: '8px',
  fontWeight: 700,
  paddingBottom: '3px',
  borderBottom: '1px solid #D1D5DB',
};

const gstHeaderCell = {
  fontSize: '7.5px',
  fontWeight: 700,
  paddingBottom: '3px',
  borderBottom: '1px solid #D1D5DB',
};

const FONT_SCALE = { small: 0.9, normal: 1, large: 1.15 };
// 80mm roll paper only has ~72mm of printable width (576 dots @ 203dpi);
// anything wider is clipped on the right. Divide by zoom so the scaled
// receipt still lands at exactly the printable width.
const PRINTABLE_WIDTH_MM = 72;

const ThermalReturnReceipt80mm = ({ receipt }) => {
  const { date, time } = dateParts(receipt?.completedAt);
  const showRate = receipt?.showRateColumn !== false;
  const decimals = receipt?.itemAmountDecimalPlaces || 2;
  const rateLabel = receipt?.rateColumnLabel || 'Rate';
  const zoom = FONT_SCALE[receipt?.receiptFontSize] ?? FONT_SCALE.normal;
  // Hiding Rate returns its 14% width to Item rather than leaving blank gutter.
  const itemColWidth = `${38 + (showRate ? 0 : 14)}%`;

  return (
    <div
      style={{
        width: `${PRINTABLE_WIDTH_MM / zoom}mm`,
        color: '#111',
        fontFamily: 'monospace',
        padding: '8px 4px',
        boxSizing: 'border-box',
        zoom,
      }}
    >
      {/* Store / Org header */}
      <div style={{ textAlign: 'center' }}>
        {receipt.logoUrl && (
          <img
            src={receipt.logoUrl}
            alt=""
            style={{ maxWidth: '120px', maxHeight: '60px', objectFit: 'contain', marginBottom: '4px', filter: 'grayscale(1) contrast(1.3)' }}
          />
        )}
        <div style={{ fontSize: '18px', fontWeight: 700 }}>{receipt.storeName}</div>
        {receipt.organizationName && (
          <div style={{ fontSize: '12px', marginTop: '4px' }}>{receipt.organizationName}</div>
        )}
        {receipt.storePhone && <div style={{ fontSize: '11px' }}>Mobile: {receipt.storePhone}</div>}
        {receipt.storeEmail && <div style={{ fontSize: '11px' }}>Email: {receipt.storeEmail}</div>}
        {receipt.gstin && (
          <div style={{ fontSize: '11px', marginTop: '2px' }}>GST NO.: {receipt.gstin}</div>
        )}
      </div>

      <div style={dividerStyle} />
      <div style={{ textAlign: 'center', fontWeight: 700, fontSize: '15px' }}>
        {receipt.invoiceTitle}
      </div>
      <div style={dividerStyle} />

      {/* Return meta */}
      <div style={{ fontSize: '11px' }}>
        <div style={rowStyle}>
          <span>Return No</span>
          <span>{receipt.returnNumber}</span>
        </div>
        {receipt.creditNoteNumber && (
          <div style={rowStyle}>
            <span>Credit Note</span>
            <span>{receipt.creditNoteNumber}</span>
          </div>
        )}
        <div style={rowStyle}>
          <span>Date</span>
          <span>{date}</span>
        </div>
        <div style={rowStyle}>
          <span>Time</span>
          <span>{time}</span>
        </div>
        <div style={rowStyle}>
          <span>Original Bill No</span>
          <span>{receipt.orderNumber}</span>
        </div>
        <div style={rowStyle}>
          <span>Refund Method</span>
          <span>{receipt.refundMethod}</span>
        </div>
      </div>

      {/* Customer */}
      {(receipt.customerName || receipt.customerMobile) && (
        <>
          <div style={dividerStyle} />
          <div style={{ fontSize: '11px' }}>
            {receipt.customerName && <div>Customer: {receipt.customerName}</div>}
            {receipt.customerMobile && <div>Mobile: {receipt.customerMobile}</div>}
            {receipt.customerGstin && <div>GSTIN: {receipt.customerGstin}</div>}
          </div>
        </>
      )}

      {/* Items */}
      <div style={dividerStyle} />
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '10px' }}>
        <thead>
          <tr>
            <th style={{ ...tableHeaderCell, textAlign: 'left', width: '6%' }}>Sr</th>
            <th style={{ ...tableHeaderCell, textAlign: 'left', width: itemColWidth }}>Item</th>
            <th style={{ ...tableHeaderCell, textAlign: 'left', width: '20%' }}>Reason</th>
            <th style={{ ...tableHeaderCell, textAlign: 'left', width: '8%' }}>Qty</th>
            {showRate && <th style={{ ...tableHeaderCell, textAlign: 'right', width: '14%' }}>{rateLabel}</th>}
            <th style={{ ...tableHeaderCell, textAlign: 'right', width: '14%' }}>Refund</th>
          </tr>
        </thead>
        <tbody>
          {receipt.items.map((item) => (
            <tr key={`${item.index}-${item.name}`}>
              <td style={{ verticalAlign: 'top', paddingTop: '4px' }}>{item.index}</td>
              <td style={{ verticalAlign: 'top', paddingTop: '4px' }}>
                <div style={{ fontSize: '10px', fontWeight: 600, lineHeight: 1.3 }}>{item.name}</div>
                {item.hsn && <div style={{ fontSize: '9px' }}>HSN: {item.hsn}</div>}
              </td>
              <td style={{ verticalAlign: 'top', paddingTop: '4px', fontSize: '9px' }}>
                {item.reason || '—'}
              </td>
              <td style={{ verticalAlign: 'top', paddingTop: '4px' }}>{item.quantity}</td>
              {showRate && (
                <td style={{ textAlign: 'right', verticalAlign: 'top', paddingTop: '4px' }}>
                  {item.rate.toFixed(decimals)}
                </td>
              )}
              <td style={{ textAlign: 'right', verticalAlign: 'top', paddingTop: '4px' }}>
                {item.amount.toFixed(decimals)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Total refund */}
      <div style={dividerStyle} />
      <div style={{ ...rowStyle, fontWeight: 700, fontSize: '12px' }}>
        <span>Total Refund</span>
        <span>{money(receipt.refundAmount)}</span>
      </div>
      {parseFloat(receipt.promotionClawbackTotal || 0) > 0 && (
        <>
          <div style={rowStyle}>
            <span>Promo Reversed{receipt.promotionClawbackName ? ` (${receipt.promotionClawbackName})` : ''}</span>
            <span>-{money(receipt.promotionClawbackTotal)}</span>
          </div>
          {receipt.debitNoteNumber && (
            <div style={{ ...rowStyle, fontSize: '9px' }}>
              <span>Debit Note</span>
              <span>{receipt.debitNoteNumber}</span>
            </div>
          )}
          <div style={{ ...rowStyle, fontWeight: 700 }}>
            <span>Net Refund</span>
            <span>{money(receipt.monetaryRefundAmount)}</span>
          </div>
        </>
      )}
      {(receipt.settlementLines || []).length > 0 ? (
        // Where the refund went + the balances after it, so the customer can
        // see why store credit is less than the note when part was paid with points.
        <>
          <div style={dividerStyle} />
          <div style={{ fontSize: '11px', fontWeight: 700, marginBottom: '4px' }}>Refund Settlement</div>
          {receipt.settlementLines.map((line, i) => (
            <div key={`${line.tenderType}-${i}`} style={rowStyle}>
              <span>
                {line.tenderType === 'loyalty_points' && line.points != null
                  ? `${line.label} (${points(line.points)})`
                  : line.label}
              </span>
              <span>{money(line.amount)}</span>
            </div>
          ))}
          {receipt.storeCreditBalanceAfter != null && (
            <div style={{ ...rowStyle, fontWeight: 700 }}>
              <span>Store Credit Balance</span>
              <span>{money(receipt.storeCreditBalanceAfter)}</span>
            </div>
          )}
          {receipt.loyaltyPointsBalanceAfter != null && (
            <div style={{ ...rowStyle, fontWeight: 700 }}>
              <span>Points Balance</span>
              <span>{points(receipt.loyaltyPointsBalanceAfter)}</span>
            </div>
          )}
        </>
      ) : parseFloat(receipt.loyaltyPointsRefunded || 0) > 0 && (
        <>
          <div style={rowStyle}>
            <span>{`${receipt.refundMethod || 'Amount'} Refunded`}</span>
            <span>{money(receipt.monetaryRefundAmount)}</span>
          </div>
          <div style={rowStyle}>
            <span>Points Returned</span>
            <span>{points(receipt.loyaltyPointsRefunded)}</span>
          </div>
        </>
      )}

      {/* GST summary */}
      {receipt.gstSummary.length > 0 && (
        <>
          <div style={dividerStyle} />
          <div style={{ fontSize: '11px', fontWeight: 700, marginBottom: '4px' }}>GST Summary</div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '9px' }}>
            <thead>
              <tr>
                <th style={{ ...gstHeaderCell, textAlign: 'left', width: '24%' }}>Tax</th>
                <th style={{ ...gstHeaderCell, textAlign: 'right', width: '19%' }}>Taxable</th>
                <th style={{ ...gstHeaderCell, textAlign: 'right', width: '19%' }}>CGST</th>
                <th style={{ ...gstHeaderCell, textAlign: 'right', width: '19%' }}>SGST</th>
                <th style={{ ...gstHeaderCell, textAlign: 'right', width: '19%' }}>IGST</th>
              </tr>
            </thead>
            <tbody>
              {receipt.gstSummary.map((row) => (
                <tr key={`${row.label}-${row.taxableAmount}`}>
                  <td style={{ paddingTop: '4px' }}>{row.label}</td>
                  <td style={{ textAlign: 'right', paddingTop: '4px' }}>
                    {row.taxableAmount.toFixed(2)}
                  </td>
                  <td style={{ textAlign: 'right', paddingTop: '4px' }}>
                    {row.cgstAmount.toFixed(2)}
                  </td>
                  <td style={{ textAlign: 'right', paddingTop: '4px' }}>
                    {row.sgstAmount.toFixed(2)}
                  </td>
                  <td style={{ textAlign: 'right', paddingTop: '4px' }}>
                    {row.igstAmount.toFixed(2)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {/* Notes */}
      {receipt.notes && (
        <>
          <div style={dividerStyle} />
          <div style={{ fontSize: '11px', fontWeight: 700, marginBottom: '3px' }}>Notes</div>
          <div style={{ fontSize: '10px', whiteSpace: 'pre-wrap' }}>{receipt.notes}</div>
        </>
      )}

      {/* Terms */}
      <div style={dividerStyle} />
      <div style={{ fontSize: '11px', fontWeight: 700, marginBottom: '4px' }}>
        Terms &amp; Conditions
      </div>
      <ol style={{ margin: 0, paddingLeft: '16px', fontSize: '9px', lineHeight: 1.5 }}>
        {receipt.terms.map((term) => (
          <li key={term}>{term}</li>
        ))}
      </ol>

      {receipt.declarationText && (
        <>
          <div style={dividerStyle} />
          <div style={{ fontSize: '11px', fontWeight: 700, marginBottom: '4px' }}>Declarations</div>
          <div style={{ fontSize: '9px', lineHeight: 1.5, textAlign: 'justify' }}>{receipt.declarationText}</div>
        </>
      )}

      {receipt.footerMessage && (
        <div style={{ textAlign: 'center', fontSize: '12px', fontWeight: 700, marginTop: '10px' }}>
          {receipt.footerMessage}
        </div>
      )}
      {receipt.websiteHandle && (
        <div style={{ textAlign: 'center', fontSize: '10px', marginTop: '2px' }}>{receipt.websiteHandle}</div>
      )}
      {receipt.instagramHandle && (
        <div style={{ textAlign: 'center', fontSize: '10px', marginTop: '2px' }}>{receipt.instagramHandle}</div>
      )}
    </div>
  );
};

export default ThermalReturnReceipt80mm;
